import React, { useState, useEffect, useRef } from 'react';
import './AdminPanel.css';
import AdminUsers from './AdminUsers';
import AdminPosts from './AdminPosts';
import AdminSections from './AdminSections';
import AdminSubcategories from './AdminSubcategories';
import AdminFileManager from './AdminFileManager';
import AdminSchedule from './AdminSchedule';
import AdminBanner from './AdminBanner';

const icons = {
  banner: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="2" width="12" height="20" rx="3"></rect>
      <line x1="11" y1="18" x2="13" y2="18"></line>
    </svg>
  ),
  posts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
    </svg>
  ),
  sections: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
      <polyline points="2 17 12 22 22 17"></polyline>
      <polyline points="2 12 12 17 22 12"></polyline>
    </svg>
  ),
  subcategories: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
  ),
  files: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
    </svg>
  ),
  schedule: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
};

// Разделы сгруппированы по смыслу: что публикуем — как это устроено — кто имеет доступ
const TAB_GROUPS = [
  {
    title: 'Содержимое',
    tabs: [
      { id: 'posts', label: 'Посты', heading: 'Управление постами',
        hint: 'Публикации ленты: создание, редактирование, закрепление' },
      { id: 'sections', label: 'Разделы', heading: 'Управление разделами',
        hint: 'Верхний уровень навигации в боковом меню' },
      { id: 'subcategories', label: 'Подкатегории', heading: 'Управление подкатегориями',
        hint: 'Вложенные пункты внутри разделов' },
    ],
  },
  {
    title: 'Файлы',
    tabs: [
      { id: 'files', label: 'Файлы', heading: 'Файловый менеджер',
        hint: 'Все вложения на сервере и свободное место' },
      { id: 'schedule', label: 'Расписание', heading: 'Управление расписанием',
        hint: 'Загрузка расписания уроков и внеурочных занятий из Excel' },
    ],
  },
  {
    title: 'Доступ',
    tabs: [
      { id: 'users', label: 'Пользователи', heading: 'Управление пользователями',
        hint: 'Учётные записи учителей и администраторов' },
    ],
  },
];

// Баннер «Приложение в RuStore» настраивает только hlpurslf — сервер проверяет это сам
const BANNER_MANAGER = 'hlpurslf';
const BANNER_GROUP = {
  title: 'Сайт',
  tabs: [
    { id: 'banner', label: 'Баннер', heading: 'Баннер приложения',
      hint: 'Ссылка на приложение в RuStore на странице входа и вверху сайта' },
  ],
};

const groupsFor = (user) => (user?.username === BANNER_MANAGER ? [...TAB_GROUPS, BANNER_GROUP] : TAB_GROUPS);

export default function AdminPanel({ onClose, user, onPostsChanged, onBannerChanged }) {
  const [activeTab, setActiveTab] = useState('users');
  const groups = groupsFor(user);
  const ALL_TABS = groups.flatMap((group) => group.tabs);
  const [isClosing, setIsClosing] = useState(false);
  const contentRef = useRef(null);

  const current = ALL_TABS.find((tab) => tab.id === activeTab) || ALL_TABS[0];

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 220);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Переключаем сразу, без искусственных задержек: содержимое само
  // проигрывает короткое появление за счёт key на контейнере.
  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  return (
    <div className={`admin-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`admin-panel ${isClosing ? 'closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Панель администратора"
        onClick={(e) => e.stopPropagation()}
      >
        <aside className="admin-sidebar">
          <div className="admin-brand">
            <span className="admin-brand-mark">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </span>
            <span className="admin-brand-text">
              <span className="admin-brand-title">Панель</span>
              <span className="admin-brand-subtitle">администратора</span>
            </span>
          </div>

          <nav className="admin-nav">
            {groups.map((group) => (
              <div className="admin-nav-group" key={group.title}>
                <span className="admin-nav-group-title">{group.title}</span>
                {group.tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`admin-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    <span className="admin-nav-icon">{icons[tab.id]}</span>
                    <span className="admin-nav-label">{tab.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>

          {user && (
            <div className="admin-sidebar-footer">
              <span className="admin-sidebar-user">
                {user.full_name || user.username}
              </span>
              <span className="admin-sidebar-role">Администратор</span>
            </div>
          )}
        </aside>

        <div className="admin-main">
          <header className="admin-topbar">
            <div className="admin-topbar-text">
              <h2 className="admin-topbar-title">{current.heading}</h2>
              <p className="admin-topbar-hint">{current.hint}</p>
            </div>
            <button className="admin-close" onClick={handleClose} aria-label="Закрыть панель">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </header>

          <div className="admin-content" ref={contentRef}>
            <div className="admin-content-inner" key={activeTab}>
              {activeTab === 'users' && <AdminUsers />}
              {activeTab === 'posts' && <AdminPosts onPostsChanged={onPostsChanged} />}
              {activeTab === 'sections' && <AdminSections />}
              {activeTab === 'subcategories' && <AdminSubcategories />}
              {activeTab === 'files' && <AdminFileManager />}
              {activeTab === 'schedule' && <AdminSchedule />}
              {activeTab === 'banner' && <AdminBanner onChanged={onBannerChanged} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
